import createReducer from "./createReducer";
import { change } from "redux-form";
import { REFINEMENTS_ACTIONS } from "./actions/Refinements";
import { SUPPRESS_SUCCESS_GROWL } from "utils/constants";
import { EDIT_VERSION_BLADE_FORM } from "forms/list";

export function setActiveVariable(variableName) {
	return ((dispatch) => {
		dispatch({
			type: REFINEMENTS_ACTIONS.SET_ACTIVE_VARIABLE,
			payload: { variableName },
		});
	});
}

export function clearActiveVariable() {
	return ((dispatch) => {
		dispatch({
			type: REFINEMENTS_ACTIONS.CLEAR_ACTIVE_VARIABLE,
		});
	});
}

export function getCropMetadataForFrame(assetId, frameWidth, frameHeight) {
	return (dispatch) => {
		return dispatch({
			type: REFINEMENTS_ACTIONS.GET_CROP_METADATA_FOR_FRAME_AJAX,
			AJAX: {
				gateway: "adstu-v2",
				version: 0,
				url: "api/v1/smart-framing/cropping-for-element",
				method: "post",
				data: {
					assetId,
					frameWidth,
					frameHeight,
				},
				success: REFINEMENTS_ACTIONS.GET_CROP_METADATA_FOR_FRAME_SUCCESS,
				error: REFINEMENTS_ACTIONS.GET_CROP_METADATA_FOR_FRAME_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		});
	};
}

export function clearCropMetadata() {
	return ((dispatch) => {
		dispatch({
			type: REFINEMENTS_ACTIONS.CLEAR_CROP_METADATA_DICTIONARY,
		});
	});
}

export function clearRefinements() {
	return ((dispatch) => {
		dispatch({
			type: REFINEMENTS_ACTIONS.CLEAR_REFINEMENTS,
		});
	});
}

export function getPreviewContent(url) {
	return (dispatch) => {
		return dispatch({
			type: REFINEMENTS_ACTIONS.GET_PREVIEW_CONTENT_AJAX,
			AJAX: {
				url,
				success: REFINEMENTS_ACTIONS.GET_PREVIEW_CONTENT_SUCCESS,
				error: REFINEMENTS_ACTIONS.GET_PREVIEW_CONTENT_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		});
	};
}

// This function updates the crop metadata setting canvas in the redux form
export function updateCropMetadata(variableName, values, crop, hasManualRefinements) {
	return (dispatch) => {
		const { [variableName]: currentVariable } = values;
		const newVariable = {
			...currentVariable,
			cropMetadata: {
				...currentVariable.cropMetadata,
				hasManualRefinements,
				canvas: crop,
			},
		};
		dispatch(change(EDIT_VERSION_BLADE_FORM, variableName, newVariable));
	};
}

export function getAssetRefinements(data) {
	return (dispatch) => {
		return dispatch({
			type: REFINEMENTS_ACTIONS.GET_ASSET_REFINEMENTS_AJAX,
			AJAX: {
				gateway: "adstu-v2",
				version: 0,
				url: "api/v1/smart-framing/bulk-cropping",
				method: "post",
				data,
				success: REFINEMENTS_ACTIONS.GET_ASSET_REFINEMENTS_SUCCESS,
				error: REFINEMENTS_ACTIONS.GET_ASSET_REFINEMENTS_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		});
	};
}

export const handlers = {
	[REFINEMENTS_ACTIONS.SET_ACTIVE_VARIABLE]: setActiveVariableName,
	[REFINEMENTS_ACTIONS.CLEAR_ACTIVE_VARIABLE]: clearActiveVariableName,
	[REFINEMENTS_ACTIONS.GET_CROP_METADATA_FOR_FRAME_SUCCESS]: addCropMetadataToDictionary,
	[REFINEMENTS_ACTIONS.CLEAR_CROP_METADATA_DICTIONARY]: clearCropMetadataDictionary,
	[REFINEMENTS_ACTIONS.GET_ASSET_REFINEMENTS_AJAX]: initAssetRefinements,
	[REFINEMENTS_ACTIONS.GET_ASSET_REFINEMENTS_SUCCESS]: setAssetRefinements,
	[REFINEMENTS_ACTIONS.GET_ASSET_REFINEMENTS_ERROR]: assetRefinementsError,
	[REFINEMENTS_ACTIONS.SET_VARIATION_REFINEMENT]: setVariationAssetRefinements,
	[REFINEMENTS_ACTIONS.SET_IMPORT_VERSIONS_REFINEMENTS]: setImportVersionsAssetRefinements,
	[REFINEMENTS_ACTIONS.CLEAR_REFINEMENTS]: clearRefinementsData,
};

function initAssetRefinements(state) {
	return {
		...state,
		assetRefinements: {},
		variationRefinements: {},
		importVersionRefinements: {},
		isAssetRefinementsLoading: true,
	};
}

function clearRefinementsData(state) {
	return {
		...state,
		assetRefinements: {},
		variationRefinements: {},
		importVersionsRefinements: {},
		isAssetRefinementsLoading: false,
	};
}

/**
 * This functions is called when we get the response from the server
 * on bulk-cropping endpoint comes. It sorts the data in a way that
 * that it can be easily accessed later on by name of asset and than by dimentions.
 * That's why we have to use loop within a loop.
 * @param state - redux state of refinements
 * @param action - redux action with payload from response
 * @returns Organized dictionary of assets with their refinements by asset name and than by dimentions.
 */
function setAssetRefinements(state, action) {
	const assets = action?.payload?.data?.assets || [];

	const assetRefinements = assets.reduce((acc, asset) => {
		acc[asset.src] = asset.refinements.reduce((refAcc, refinement) => {
			refAcc[refinement.name] = refinement.canvas;
			return refAcc;
		}, {});
		return acc;
	}, {});

	return {
		...state,
		assetRefinements,
		isAssetRefinementsLoading: false,
	};
}

function assetRefinementsError(state) {
	return {
		...state,
		assetRefinements: {},
		variationRefinements: {},
		importVersionsRefinements: {},
		isAssetRefinementsLoading: false,
	};
}

function setVariationAssetRefinements(state, action) {
	const { index, dimensions, canvas } = action.payload || {};
	const { variationRefinements } = state;
	const refinements = variationRefinements[index] || {};

	return {
		...state,
		variationRefinements: {
			...variationRefinements,
			[index]: {
				...refinements,
				[dimensions]: canvas,
			},
		},
	};
}

/**
 *
 * Organizes Imported Versions Refinements in a way
 * that it can be easily accessed later on by creativeId
 * and then by versionName and variable name.
 * @param {*} state
 * @param {*} action
 * @returns
 */
function setImportVersionsAssetRefinements(state, action) {
	const { creativeId, versionName, values } = action.payload || {};
	const { importVersionsRefinements } = state;
	const creativeRefinements = importVersionsRefinements[creativeId] || {};

	return {
		...state,
		importVersionsRefinements: {
			...importVersionsRefinements,
			[creativeId]: {
				...creativeRefinements,
				[versionName]: {
					...values,
				},
			},
		},
	};
}

const initialRefinementsState = {
	activeVariableName: null,
	assetRefinements: {},
	variationRefinements: {},
	importVersionsRefinements: {},
	cropMetadataDictionary: {},
};

function setActiveVariableName(state, action) {
	return {
		...state,
		activeVariableName: action.payload.variableName,
	};
}

function clearActiveVariableName(state) {
	return {
		...state,
		activeVariableName: null,
	};
}

function addCropMetadataToDictionary(state, action) {
	return {
		...state,
		cropMetadataDictionary: {
			...state.cropMetadataDictionary,
			[`${action.AJAX.data.assetId}_${action.AJAX.data.frameWidth}_${action.AJAX.data.frameHeight}`]: action.payload.data.cropMetadata,
		},
	};
}

function clearCropMetadataDictionary(state) {
	return {
		...state,
		cropMetadataDictionary: {},
	};
}

export function setVariationRefinement(index, dimensions, canvas) {
	return ((dispatch) => {
		dispatch({
			type: REFINEMENTS_ACTIONS.SET_VARIATION_REFINEMENT,
			payload: { index, dimensions, canvas },
		});
	});
}

// This will save any custom version by creativeId and versionName
// for the imported versions and easy extraction later on
// Version name and not id is used as during the import
// some versions may be newly created and not have id yet.
export function setImportVersionsRefinement(creativeId,
	versionName,
	values) {
	return ((dispatch) => {
		dispatch({
			type: REFINEMENTS_ACTIONS.SET_IMPORT_VERSIONS_REFINEMENTS,
			payload: { creativeId, versionName, values },
		});
	});
}

let reducerConfig = {
	typeRoot: "REFINEMENTS",
	actionTypes: REFINEMENTS_ACTIONS,
	actionCreators: {
		setActiveVariable,
		clearActiveVariable,
	},
	initialState: initialRefinementsState,
	handlers,
};

export default createReducer(reducerConfig.initialState, reducerConfig.handlers);
export const Refinements = reducerConfig.actionCreators;

